"use client";

import type { ServiceAuth } from "@peerspace/bellhop";
import { configureServices } from "@peerspace/bellhop";
import { getCookie } from "cookies-next/client";

import { ACCESS_TOKEN_COOKIE } from "~/utils/constants";

/**
 * Client-side bellhop registry.
 *
 * Next.js splits server and client bundles; bellhop's service-registry is
 * module-scoped, so each bundle needs its own `configureServices` call. The
 * server registry lives in `~/bellhop` (server-only, with server baseURLs and
 * handshake secrets); this file registers the client-facing services with
 * client baseURLs and a cookie-based user-token reader.
 *
 * Imported for its side effect at the top of `Providers` so `configureServices`
 * runs before any hook or effect calls a bellhop endpoint from the browser.
 */

// Read the PSAccess bearer from the browser cookie. Returns undefined when the
// viewer isn't signed in — bellhop turns that into `HttpError({ kind: "auth" })`
// so callers can distinguish "not signed in" from other HTTP failures.
const getUserToken = (): string | undefined => {
  const value = getCookie(ACCESS_TOKEN_COOKIE);
  return typeof value === "string" && value.length > 0 ? value : undefined;
};

// One shared user-auth config for every service that authenticates the viewer
// via the PSAccess cookie. All services below reuse this same reference — one
// place to update if the client-side auth mechanism ever changes.
const clientUserAuth: ServiceAuth = {
  user: { getToken: getUserToken },
};

configureServices({
  messaging: {
    baseURL: { client: "" },
    auth: clientUserAuth,
  },
  metrics: {
    baseURL: { client: "" },
    auth: clientUserAuth,
  },
  profile: {
    baseURL: { client: "" },
    auth: clientUserAuth,
  },
  auth: {
    baseURL: { client: "" },
    auth: clientUserAuth,
  },
  // fe-lb exposes spaces-v2 to the browser under `/v2` (`/v2/spaces-v2/...`).
  spaces: {
    baseURL: { client: "/v2" },
    auth: clientUserAuth,
  },
});
